import { calculateHeaderGroupHeight } from "@theme/critical";
import { Component } from "@theme/component";
import { onDocumentLoaded, changeMetaThemeColor } from "@theme/utilities";

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ["headerDrawerContainer", "headerMenu", "headerRowTop"];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * The last recorded scrollTop of the document
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout for animation delay
   * @type {number | null}
   */
  #timeout = null;

  /**
   * Tracks the current header design state
   * @type {boolean}
   */
  #isCompact = false;

  /**
   * Request animation frame ID for scroll handling
   * @type {number | null}
   */
  #rafId = null;

  /**
   * Pending scroll update flag
   * @type {boolean}
   */
  #scrollPending = false;

  /**
   * Currently open dropdown reference
   * @type {HTMLElement | null}
   */
  #activeDropdown = null;

  /**
   * Flag to track if cart drawer has been initialized
   * @type {boolean}
   */
  #cartDrawerInitialized = false;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty("--header-height", `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }

    // On mobile, ensure cart is in header (only if not already initialized)
    if (window.innerWidth < 750 && !this.#cartDrawerInitialized) {
      this.#ensureCartInHeader();
    }
  });

  /**
   * Ensures cart drawer is in the header (for mobile)
   * Removes duplicates and ensures only one cart drawer exists in header-actions
   * Optimized to run only once
   */
  #ensureCartInHeader = () => {
    if (this.#cartDrawerInitialized) return;

    const headerTarget = /** @type {HTMLElement | null} */ (document.querySelector("header-actions"));
    if (!headerTarget) return;

    const allCartDrawers = Array.from(document.querySelectorAll("cart-drawer-component"));
    if (allCartDrawers.length === 0) return;

    const isMobile = window.innerWidth < 750;

    // Find the cart drawer that's already in header-actions, or get the first one
    let cartDrawer = /** @type {HTMLElement | null} */ (headerTarget.querySelector("cart-drawer-component"));

    if (!cartDrawer) {
      cartDrawer = /** @type {HTMLElement} */ (allCartDrawers[0]);
    }

    // On mobile, hide all cart drawers initially
    if (isMobile) {
      allCartDrawers.forEach((drawer) => {
        const drawerEl = /** @type {HTMLElement} */ (drawer);
        drawerEl.style.opacity = "0";
        drawerEl.style.visibility = "hidden";
      });
    }

    // Remove duplicates only if multiple exist
    if (allCartDrawers.length > 1) {
      allCartDrawers.forEach((drawer) => {
        if (drawer !== cartDrawer) {
          drawer.remove();
        }
      });
    }

    // Move cart drawer to header-actions if not already there
    if (cartDrawer && cartDrawer.parentElement !== headerTarget) {
      headerTarget.appendChild(cartDrawer);
    }

    // On mobile, show cart drawer after ensuring it's in the correct position
    if (isMobile && cartDrawer) {
      requestAnimationFrame(() => {
        const drawerEl = /** @type {HTMLElement} */ (cartDrawer);
        drawerEl.style.opacity = "1";
        drawerEl.style.visibility = "visible";
        drawerEl.style.transition = "";
        this.#cartDrawerInitialized = true;
      });
    } else if (cartDrawer) {
      const drawerEl = /** @type {HTMLElement} */ (cartDrawer);
      drawerEl.style.opacity = "1";
      drawerEl.style.transition = "";
      this.#cartDrawerInitialized = true;
    }
  };

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        // For scroll-up mode, track if header is offscreen
        if (!isIntersecting && this.dataset.stickyState !== "active") {
          this.dataset.stickyState = "active";
        }
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove("desktop:hidden");
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add("hidden");
    } else {
      this.refs.headerDrawerContainer.classList.add("desktop:hidden");
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove("hidden");
    }
  }

  /**
   * Handles header design state based on scroll position
   */
  #updateHeaderDesign = () => {
    // Stop scroll functionality when inline search is active (check all instances)
    const searchWrappers = document.querySelectorAll("inline-search-wrapper");
    const isSearchActive = Array.from(searchWrappers).some((wrapper) => wrapper.classList.contains("is-active"));

    if (isSearchActive) {
      return;
    }

    const scrollY = window.scrollY;
    const threshold = 50;

    // Hysteresis: different thresholds for scrolling up vs down
    const shouldBeCompact = this.#isCompact
      ? scrollY > 40 // When compact, need to scroll above 40px to expand
      : scrollY > 60; // When expanded, need to scroll below 60px to compact

    if (shouldBeCompact !== this.#isCompact) {
      this.#isCompact = shouldBeCompact;

      if (window.innerWidth >= 750) {
        this.classList.toggle("header--scrolled", shouldBeCompact);
        this.classList.toggle("header--at-top", !shouldBeCompact);
      }

      // Sync scroll state to body
      document.body.classList.toggle("header-scrolled", shouldBeCompact);
    }
  };

  /**
   * Updates scroll direction attribute
   * @param {number} scrollTop - Current scroll position
   * @param {boolean} isScrollingUp - Scroll direction
   */
  #updateScrollDirection = (scrollTop, isScrollingUp) => {
    const isAtTop = this.getBoundingClientRect().top >= 0;

    if (isAtTop) {
      this.dataset.scrollDirection = "none";
    } else {
      this.dataset.scrollDirection = isScrollingUp ? "up" : "down";
    }
  };

  /**
   * Handles scroll for sticky='always' mode
   */
  #handleAlwaysScroll = () => {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;

    this.#updateHeaderDesign();
    this.#updateScrollDirection(scrollTop, isScrollingUp);

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Handles scroll for sticky='scroll-up' mode
   */
  #handleScrollUpMode = () => {
    // Stop scroll functionality when inline search is active (check all instances)
    const searchWrappers = document.querySelectorAll("inline-search-wrapper");
    const isSearchActive = Array.from(searchWrappers).some((wrapper) => wrapper.classList.contains("is-active"));

    if (isSearchActive) {
      return;
    }

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const scrollDelta = Math.abs(scrollTop - this.#lastScrollTop);
    const isScrollingUp = scrollTop < this.#lastScrollTop;

    this.#updateHeaderDesign();

    // Only hide/show if we've scrolled past the header height and with a reasonable threshold
    const headerHeight = this.getBoundingClientRect().height;
    const hideThreshold = headerHeight + 50; // Need to scroll past header + 50px

    if (scrollDelta >= 10 && scrollTop > hideThreshold) {
      if (isScrollingUp) {
        // Show header when scrolling up
        this.dataset.stickyState = "active";
        this.dataset.scrollDirection = "up";
      } else {
        // Hide header when scrolling down
        this.dataset.stickyState = "idle";
        this.dataset.scrollDirection = "down";
      }
    } else if (scrollTop <= hideThreshold) {
      // Always show header when near the top
      this.dataset.stickyState = "active";
      this.dataset.scrollDirection = "none";
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Optimized scroll handler with RAF
   */
  #handleWindowScroll = () => {
    if (this.#scrollPending) return;

    this.#scrollPending = true;

    this.#rafId = requestAnimationFrame(() => {
      const stickyMode = this.getAttribute("sticky");

      if (stickyMode === "always") {
        this.#handleAlwaysScroll();
      } else if (stickyMode === "scroll-up") {
        this.#handleScrollUpMode();
      }

      // Close active dropdown on scroll
      this.#closeActiveDropdown();

      this.#scrollPending = false;
    });
  };

  /**
   * Handle dropdown toggle
   * @param {Event} event
   */
  #handleDropdownToggle = (event) => {
    const button = /** @type {HTMLElement | null} */ (event.currentTarget);
    if (!button) return;

    const dropdownItem = button.closest(".overflow-menu-item, .header__nav-overflow-item");
    if (!dropdownItem) return;

    const dropdown = /** @type {HTMLElement | null} */ (
      dropdownItem.querySelector(".overflow-dropdown, .header__nav-dropdown")
    );
    if (!dropdown) return;

    event.stopPropagation();

    // Close if clicking the same dropdown
    if (this.#activeDropdown === dropdown) {
      this.#closeActiveDropdown();
      return;
    }

    // Close any open dropdown first
    this.#closeActiveDropdown();

    // Open new dropdown
    dropdown.classList.add("is-open");
    button.setAttribute("aria-expanded", "true");
    this.#activeDropdown = dropdown;
  };

  /**
   * Close the active dropdown
   */
  #closeActiveDropdown = () => {
    if (!this.#activeDropdown) return;

    const dropdownItem = this.#activeDropdown.closest(".overflow-menu-item, .header__nav-overflow-item");
    const button = dropdownItem?.querySelector(".overflow-menu-btn, .header__nav-overflow-btn");

    this.#activeDropdown.classList.remove("is-open");
    if (button) button.setAttribute("aria-expanded", "false");
    this.#activeDropdown = null;
  };

  /**
   * Handle clicks outside dropdowns
   * @param {Event} event
   */
  #handleClickOutside = (event) => {
    if (!this.#activeDropdown) return;

    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target) return;

    const isClickInside =
      this.#activeDropdown.contains(target) || target.closest(".overflow-menu-btn, .header__nav-overflow-btn");

    if (!isClickInside) {
      this.#closeActiveDropdown();
    }
  };

  /**
   * Initialize dropdown event listeners
   */
  #initDropdowns = () => {
    // Header center menu dropdowns
    const overflowButtons = this.querySelectorAll(".overflow-menu-btn");
    overflowButtons.forEach((button) => {
      button.addEventListener("click", this.#handleDropdownToggle);
    });

    // Yellow nav dropdowns
    const navButtons = this.querySelectorAll(".header__nav-overflow-btn");
    navButtons.forEach((button) => {
      button.addEventListener("click", this.#handleDropdownToggle);
    });

    // Close on outside click
    document.addEventListener("click", this.#handleClickOutside);
  };

  connectedCallback() {
    super.connectedCallback();

    // On mobile, ensure cart drawer is properly positioned after DOM is ready (only once)
    if (window.innerWidth < 750 && !this.#cartDrawerInitialized) {
      if (document.readyState === "loading") {
        document.addEventListener(
          "DOMContentLoaded",
          () => {
            this.#ensureCartInHeader();
          },
          { once: true }
        );
      } else {
        requestAnimationFrame(() => {
          this.#ensureCartInHeader();
        });
      }
    }

    this.#resizeObserver.observe(this);
    this.addEventListener("overflowMinimum", this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute("sticky");

    // Initialize scroll state immediately to prevent flash
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    this.#lastScrollTop = scrollTop;

    // Set initial header state based on scroll position
    const shouldBeScrolled = scrollTop > 50;
    this.#isCompact = shouldBeScrolled;
    document.body.classList.toggle("header-scrolled", shouldBeScrolled);

    if (window.innerWidth >= 750) {
      this.classList.toggle("header--scrolled", shouldBeScrolled);
      this.classList.toggle("header--at-top", !shouldBeScrolled);
    }

    // Initialize sticky mode state
    if (stickyMode === "scroll-up") {
      const headerHeight = this.getBoundingClientRect().height;
      const hideThreshold = headerHeight + 50;
      if (scrollTop > hideThreshold) {
        this.dataset.stickyState = "idle";
        this.dataset.scrollDirection = "down";
      } else {
        this.dataset.stickyState = "active";
        this.dataset.scrollDirection = "none";
      }
    } else if (stickyMode === "always") {
      this.dataset.scrollDirection = this.getBoundingClientRect().top >= 0 ? "none" : "down";
    }

    // Refine state after browser restores scroll position
    requestAnimationFrame(() => {
      const restoredScrollTop = document.scrollingElement?.scrollTop ?? 0;
      if (restoredScrollTop !== scrollTop) {
        this.#lastScrollTop = restoredScrollTop;
        if (stickyMode === "always") {
          this.#handleAlwaysScroll();
        } else if (stickyMode === "scroll-up") {
          this.#handleScrollUpMode();
        }
      } else {
        this.#updateHeaderDesign();
      }
    });

    window.addEventListener("scroll", this.#handleWindowScroll, { passive: true });

    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === "always");
    }

    this.#initDropdowns();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener("overflowMinimum", this.#handleOverflowMinimum);
    window.removeEventListener("scroll", this.#handleWindowScroll);
    document.removeEventListener("click", this.#handleClickOutside);

    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
    }
    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    document.body.style.setProperty("--header-height", "0px");
    document.body.classList.remove("header-scrolled");

    // Ensure cart returns to header
    this.#ensureCartInHeader();
  }
}

if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}
